import "./App.scss";
import { useEffect, useState } from "react";
import Footer from "./common/components/footer/footer";
import Navbar from "./common/components/navbar/navbar";
import Context from "./common/components/utils/context";
import { useIsAuthenticated } from "@azure/msal-react";
import { Pages } from "./common/components/route/route";
import {
  AccountsV1Service,
  NotificationsV1Service,
  ShapeFilesV1Service,
} from "./common/services/api/api";
import { IHomepageSections } from "./components/posts/models";
import ErrorBoundary from "./common/components/errorBoundary";
import { Alert, IAlert } from "./common/components/utils/alert";
import { IAccount } from "./common/models/accounts";

import { ToastContainer } from "react-toastify";
import { IShapeFile } from "./components/earthtwin/models";
import { IModal, Modal } from "./common/components/modal/modal";
import { NotificationService } from "./common/services/notificationService";
import { useNavigate } from "react-router-dom";
import { environment } from "./common/env";

const App = () => {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  // CONTEXT
  const [context, setContext] = useState({
    account: {} as IAccount,
    setAccount,
    shapeFiles: [] as IShapeFile[],
    setShapeFiles,
    homepageSections: {} as IHomepageSections,
    setHomepageSections,
    alert: {} as IAlert,
    setAlert,
    modal: {} as IModal,
    setModal,
    cryptedUserID: "" as string,
    setCryptedUserID,
    cryptedCompanyID: "" as string,
    setCryptedCompanyID,
    cryptedRole: "" as string,
    setCryptedRole,
    notificationCount: 0 as number,
    setNotificationCount,
    setCurrentPosition,
    currentPosition: { latitude: 0, longitude: 0, altitude: 0 } as {
      latitude: number;
      longitude: number;
      altitude: number;
    },
  });

  // GLOBAL FUNCTIONS

  function setAccount(data: IAccount) {
    context.account = data;
    setContext(Object.assign({}, context, context.account));
  }
  function setShapeFiles(data: IShapeFile[]) {
    context.shapeFiles = data;
    setContext(Object.assign({}, context, context.shapeFiles));
  }
  function setHomepageSections(data: IHomepageSections) {
    context.homepageSections = data;
    setContext(Object.assign({}, context, context.homepageSections));
  }
  function setAlert(data: IAlert) {
    context.alert = data;
    setContext(Object.assign({}, context, context.alert));
  }
  function setModal(data: IModal) {
    context.modal = data;
    setContext(Object.assign({}, context, context.modal));
  }
  function setCryptedUserID(data: string) {
    context.cryptedUserID = data;
    setContext(Object.assign({}, context, context.cryptedUserID));
  }
  function setCryptedCompanyID(data: string) {
    context.cryptedCompanyID = data;
    setContext(Object.assign({}, context, context.cryptedCompanyID));
  }
  function setCryptedRole(data: string) {
    context.cryptedRole = data;
    setContext(Object.assign({}, context, context.cryptedRole));
  }
  function setNotificationCount(data: number) {
    context.notificationCount = data;
    setContext(Object.assign({}, context, context.notificationCount));
  }
  function setCurrentPosition(latitude: number, longitude: number, altitude: number) {
    context.currentPosition = { latitude, longitude, altitude };
    setContext(Object.assign({}, context, context.currentPosition));
  }

  useEffect(() => {
    console.log("API_URL:" + environment.API_URL);
    console.log("REACT_APP_AD_B2C_REACT_REDIRECT_URI:" + environment.AD_B2C_ReactRedirectUri);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      try {
        new AccountsV1Service()
          .get()
          .then((data) => context.setAccount(data.data));
      } catch (error) {
        console.error("Error fetching account:", error);
      }

      try {
        new NotificationsV1Service()
          .unreadCount()
          .then((data) => context.setNotificationCount(data.data));
      } catch (error) {
        console.error("Error fetching NotificationCount:", error);
      }

      try {
        new ShapeFilesV1Service().getShapeFiles().then((data) => {
          context.setShapeFiles(data.data);
        });
      } catch (error) {
        console.error("Error fetching shape files:", error);
      }

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;
            const altitude = position.coords.altitude || 0;

            context.setCurrentPosition(latitude, longitude, altitude);
            console.log(`Current position: ${latitude}, ${longitude}, ${altitude}`);
          },
          (error) => {
            console.error(`Error getting location: ${error.message}`);
          },
          {
            enableHighAccuracy: true, // Use GPS if available for more accurate results
            timeout: 5000, // Maximum time to wait for a response
            maximumAge: 0, // Do not use cached position
          }
        );
      } else {
        console.log("Geolocation not supported!");
      }

      notificationsRegister();
    }
  }, [isAuthenticated]);

  const notificationsRegister = () => {
    var notificationService = new NotificationService();
    notificationService.initialize(context, navigate);
  };

  return (
    <Context.Provider value={context}>
      <div className="flex flex-col h-screen justify-between">
        <ToastContainer />
        <Navbar />
        <ErrorBoundary>
          <Alert />
          <Pages />
          <Modal {...context.modal} />
        </ErrorBoundary>
        <Footer />
      </div>
    </Context.Provider>
  );
};

export default App;
